import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useEffect, useState } from 'react';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addFipsCode'
  | 'bulkAddFipsCodes'
  | 'getFIPSCodes'
  | 'getFIPSCode'
  | 'updateFipsCode';

export const useFipsCodes = () => {
  const [action, setAction] = useState<Action>('');
  const [bulkFipsCodes, setBulkFipsCodes] = useState<BulkFipsCodes>();
  const [fipsCodes, setFipsCodes] = useState<FipsCode[]>([]);
  const [fipsCode, setFipsCode] = useState<FipsCode | null>(null);
  const [oldFipsCode, setOldFipsCode] = useState<string | null>(null);
  const { id: operatorId, fips, updateOperatorState } = useOperator();
  const { loading, makeRequest, response } = useFetch();
  const { sortAsc } = useSort<FipsCode>('county');
  const BASE_URL = `${BASE_URL_MAP['Residential']}/${operatorId}/FipsCodes`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'bulkAddFipsCodes') {
        const bulkResponse = mapBulkResponse(response);
        setBulkFipsCodes(bulkResponse);
        updateOperatorState('fips', [
          ...(fips ?? []).concat(bulkResponse.success).sort(sortAsc),
        ]);
      } else if (action === 'updateFipsCode') {
        const mappedFipsCode = mapFIPSCode(response);
        updateOperatorState(
          'fips',
          [
            mappedFipsCode,
            ...(fips ?? []).filter((fipsCode) => {
              if (oldFipsCode) return fipsCode.code !== oldFipsCode;
              return fipsCode.code !== mappedFipsCode.code;
            }),
          ].sort(sortAsc)
        );
        setOldFipsCode(null);
      } else if (action === 'addFipsCode') {
        const mappedFipsCode = mapFIPSCode(response);
        updateOperatorState(
          'fips',
          [mappedFipsCode, ...(fips ?? [])].sort(sortAsc)
        );
      } else if (action === 'getFIPSCodes')
        setBulkFipsCodes(
          response.map((fipsCode: ApiFipsCode) => mapFIPSCode(fipsCode))
        );
    }
  }, [response, loading]);

  const addFipsCode = (fipsCode: CreateFipsCode) => {
    setAction('addFipsCode');
    const body = mapApiFipsCode(fipsCode);

    makeRequest(`${BASE_URL}`, 'post', sanitizeValue(body));
  };

  const bulkAddFipsCodes = (fipsCodes: CreateFipsCode[]) => {
    setAction('bulkAddFipsCodes');
    const body = fipsCodes.map((fipsCode) => mapApiFipsCode(fipsCode));

    makeRequest(`${BASE_URL}/BulkAdd`, 'post', sanitizeValue(body));
  };

  const clearBulkAdds = () => setBulkFipsCodes({ success: [], fail: [] });

  const getFIPSCodes = () => {
    setAction('getFIPSCodes');
    makeRequest(BASE_URL, 'get');
  };

  const getFIPSCode = (code: string) => {
    setAction('getFIPSCode');
    makeRequest(`/${BASE_URL}/${code}`, 'get');
  };

  const updateFipsCode = (fipsCode: FipsCode) => {
    setAction('updateFipsCode');
    if (fipsCode.oldId !== undefined) setOldFipsCode(fipsCode.oldId);
    const body = mapApiFipsCode(fipsCode);

    makeRequest(
      `${BASE_URL}/${
        fipsCode.oldId !== undefined ? fipsCode.oldId : fipsCode.code
      }`,
      'put',
      sanitizeValue(body)
    );
  };

  const resetState = () => {
    setAction('');
    setBulkFipsCodes({ success: [], fail: [] });
    setFipsCodes([]);
    setFipsCode(null);
    setOldFipsCode(null);
  };

  return {
    addFipsCode,
    bulkFipsCodes,
    bulkAddFipsCodes,
    clearBulkAdds,
    fipsCode,
    fipsCodes,
    getFIPSCode,
    getFIPSCodes,
    loading,
    updateFipsCode,
  };
};

const mapBulkResponse = (
  response: ApiBulkResponse<ApiFipsCode, ApiFipsCode>
) => ({
  success: response.Success.map((fipsCode) => mapFIPSCode(fipsCode)),
  fail: response.Fail.map((fipsCode) => mapFIPSCode(fipsCode)),
});

const mapApiFipsCode = (fipsCode: FipsCode): ApiFipsCode => ({
  Active: fipsCode.active,
  Code: fipsCode.code,
  CountyName: fipsCode.county,
});

export const mapFIPSCode = (code: ApiResFipsCode): FipsCode => ({
  active: code.Active,
  code: code.Code,
  county: code.CountyName,
});
